import { useCallback, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AppConfig from '../../utils/appconfig';
import { Navbar } from '../../components/navbar';
import { Footer } from '../../components/footer';
import { SectionTitle } from '../../components/sectionTitle';
import { scrollBodyToTop } from '../../utils/utils';
import { useGalleryFolder } from '../../hooks/useGalleryFolder';
import { crumbsFromPath, folderPathFromRoute, galleryRoute, GalleryPhoto, parentPath } from '../../lib/gallery/api';
import { describeAlbum, pluralPl } from '../../lib/gallery/format';
import { GalleryBreadcrumb } from './components/galleryBreadcrumb';
import { AlbumGrid, AlbumGridVariant } from './components/albumGrid';
import { PhotoGrid } from './components/photoGrid';
import { Lightbox } from './components/lightbox';
import { ArchiveLinks } from './components/archiveLinks';

const currentPage: string = 'galeria';

const GallerySkeleton = ({ heroes }: { heroes?: boolean }) => (
    <div className={`galeria__albums${heroes ? ' galeria__albums--heroes' : ''}`} aria-hidden="true">
        {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="galeria__skeleton" />)}
    </div>
);

function headerMeta(folder: { albums: unknown[]; photos: unknown[] } | null, isRoot: boolean): string | null {
    if (!folder) return null;
    if (isRoot) {
        const n = folder.albums.length;
        if (n === 0) return null;
        return `${n} ${pluralPl(n, 'album', 'albumy', 'albumów')}`;
    }
    const text = describeAlbum(folder.photos.length, folder.albums.length);
    return text === 'Pusty album' ? null : text;
}

export function Galeria() {
    const path = folderPathFromRoute(useParams()['*']);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const foto = searchParams.get('foto');

    const isRoot = path === '';
    const state = useGalleryFolder(path);
    const folder = state.status === 'ready' ? state.data : null;
    const folderName = path ? path.split('/').pop() : '';
    const crumbs = crumbsFromPath(path);
    const meta = headerMeta(folder, isRoot);

    useEffect(() => {
        document.title = `${folderName ? `${folderName} - ` : ''}Galeria - ${AppConfig.baseTabTitle}`;
    }, [folderName]);

    useEffect(() => {
        scrollBodyToTop();
    }, [path]);

    // Podgląd zdjęcia żyje w adresie (?foto=nazwa.jpg): można go linkować, a „wstecz” go zamyka.
    const photoIndex = folder && foto ? folder.photos.findIndex((p) => p.name === foto) : -1;

    const openPhoto = useCallback((photo: GalleryPhoto) => {
        navigate({ search: `?foto=${encodeURIComponent(photo.name)}` }, { state: { lightbox: true } });
    }, [navigate]);

    const navigatePhoto = useCallback((index: number) => {
        if (!folder) return;
        navigate({ search: `?foto=${encodeURIComponent(folder.photos[index].name)}` }, { replace: true, state: location.state });
    }, [navigate, folder, location.state]);

    const closePhoto = useCallback(() => {
        if ((location.state as { lightbox?: boolean } | null)?.lightbox) navigate(-1);
        else navigate({ search: '' }, { replace: true });
    }, [navigate, location.state]);

    const title = path
        ? <span className="text-color--primary">{folderName}</span>
        : <>Hard Adv <span className="text-color--primary">Galeria</span></>;

    const isEmpty = !!folder && folder.albums.length === 0 && folder.photos.length === 0;

    return (
        <div className="App theme-light">
            <Navbar currentPage={currentPage} />

            <div className="content-wrapper galeria__content-wrapper">
                <header className="galeria__header">
                    {path && <GalleryBreadcrumb crumbs={crumbs} />}
                    <SectionTitle title={title} customClassName={'section-title__wrapper--home galeria__title'} />
                    {meta && (
                        <div className="galeria__rule" aria-hidden={false}>
                            <span>{meta}</span>
                        </div>
                    )}
                    {path && (
                        <Link className="galeria__up" to={galleryRoute(parentPath(path))}>← Wyżej</Link>
                    )}
                </header>

                <main className={`galeria__main${isRoot ? ' galeria__main--root' : ''}`}>
                    {state.status === 'loading' && <GallerySkeleton heroes={isRoot} />}

                    {state.status === 'error' && (
                        <div className="galeria__status" role="alert">
                            {state.error.isNotFound ? (
                                <>
                                    <p className="galeria__status-title">Nie znaleziono albumu</p>
                                    <p>Album nie istnieje albo został przeniesiony.</p>
                                    <Link className="galeria__up" to="/galeria">← Wróć do galerii</Link>
                                </>
                            ) : (
                                <>
                                    <p className="galeria__status-title">Nie udało się wczytać galerii</p>
                                    <p>{state.error.message}</p>
                                    <div className="custom-button__link-wrapper galeria__retry">
                                        <button type="button" className="custom-button" onClick={state.retry}>Spróbuj ponownie</button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {isEmpty && <p className="galeria__status">Ten album jest pusty.</p>}

                    {folder && folder.albums.length > 0 && (
                        <section className="galeria__section">
                            {folder.photos.length > 0 && <h3 className="galeria__subtitle">Albumy</h3>}
                            <AlbumGrid
                                albums={folder.albums}
                                variant={(
                                    isRoot ? 'heroes' :
                                    folder.photos.length === 0 ? 'strips' :
                                    'default'
                                ) as AlbumGridVariant}
                            />
                        </section>
                    )}

                    {folder && folder.photos.length > 0 && (
                        <section className="galeria__section">
                            {folder.albums.length > 0 && <h3 className="galeria__subtitle">Zdjęcia</h3>}
                            <PhotoGrid
                                key={`${folder.path}:${folder.layout ?? 'grid'}`}
                                photos={folder.photos}
                                onOpen={openPhoto}
                                layout={folder.layout}
                            />
                        </section>
                    )}
                </main>

                {isRoot && <ArchiveLinks />}
            </div>

            {folder && photoIndex >= 0 && (
                <Lightbox photos={folder.photos} index={photoIndex} onClose={closePhoto} onNavigate={navigatePhoto} />
            )}

            <Footer currentPage={currentPage} />
        </div>
    );
}
